/**
 * Converts mermaid code fences and HTML code blocks into
 * `<div data-mermaid-block>` elements that TipTap's MermaidBlock
 * extension can parse as custom nodes.
 *
 * Handles three input patterns:
 * 1. Markdown fences: ` ```mermaid ... ``` `
 * 2. HTML: `<pre><code class="language-mermaid">...</code></pre>`
 * 3. Bare: `<code class="language-mermaid">...</code>`
 *
 * @param {string} content - Raw HTML or markdown content.
 * @returns {string} Content with mermaid blocks converted.
 */
export const convertMermaidBlocks = (content) => {
  if (!content) return content;
  let result = content;

  const escapeAttr = (raw) =>
    raw
      .trim()
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

  const decodeHtml = (raw) =>
    raw
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&')
      .replace(/&quot;/g, '"');

  // Pattern 1: markdown fences ```mermaid ... ```
  result = result.replace(
    /```mermaid\s*\n([\s\S]*?)```/gi,
    (_, code) =>
      `<div data-mermaid-block="true" data-mermaid-code="${escapeAttr(code)}"></div>`,
  );

  // Pattern 2: <pre><code class="language-mermaid">...</code></pre>
  result = result.replace(
    /<pre[^>]*>\s*<code[^>]*class="[^"]*language-mermaid[^"]*"[^>]*>([\s\S]*?)<\/code>\s*<\/pre>/gi,
    (_, code) =>
      `<div data-mermaid-block="true" data-mermaid-code="${escapeAttr(decodeHtml(code))}"></div>`,
  );

  // Pattern 3: bare <code class="language-mermaid">...</code>
  result = result.replace(
    /<code[^>]*class="[^"]*language-mermaid[^"]*"[^>]*>([\s\S]*?)<\/code>/gi,
    (_, code) =>
      `<div data-mermaid-block="true" data-mermaid-code="${escapeAttr(decodeHtml(code))}"></div>`,
  );

  return result;
};

/**
 * Replace `<div data-mermaid-block ...>...</div>` elements with short
 * placeholder tokens (`{{MERMAID_1}}`, etc.) so the full mermaid source
 * is not sent to the LLM. Returns the simplified HTML and a map that
 * {@link restoreMermaidAliases} can use to restore the originals.
 *
 * @param {string} html - Document HTML that may contain mermaid blocks.
 * @returns {{ aliasedHtml: string, mermaidMap: Record<string, string> }}
 */
export const aliasMermaidBlocks = (html) => {
  if (!html) return { aliasedHtml: html, mermaidMap: {} };

  const mermaidMap = {};
  let counter = 0;

  const aliasedHtml = html.replace(
    /<div[^>]*data-mermaid-block[^>]*>[\s\S]*?<\/div>/gi,
    (match) => {
      counter++;
      const alias = `{{MERMAID_${counter}}}`;
      mermaidMap[alias] = match;
      return alias;
    },
  );

  return { aliasedHtml, mermaidMap };
};

/**
 * Restore mermaid placeholder tokens back to the original
 * `<div data-mermaid-block>` elements.
 *
 * @param {string} html - HTML containing `{{MERMAID_N}}` placeholders.
 * @param {Record<string, string>} mermaidMap - Map from {@link aliasMermaidBlocks}.
 * @returns {string} HTML with mermaid blocks restored.
 */
export const restoreMermaidAliases = (html, mermaidMap) => {
  if (!html || !mermaidMap) return html;

  let restored = html;
  for (const [alias, original] of Object.entries(mermaidMap)) {
    restored = restored.split(alias).join(original);
  }
  return restored;
};
